/**
 * Style factory for VideoSelectorModal and its tab subcomponents
 * (VideoSelectorLibraryTab / VideoSelectorExternalUrlTab).
 */
import type { CSSProperties } from 'react';
import type { ThemeTokens } from '@course-authoring/shared-types';

export type VideoSelectorModalStyles = Record<string, CSSProperties>;

export function createVideoSelectorModalStyles(tokens: ThemeTokens): VideoSelectorModalStyles {
  return {
    // ── Header ──────────────────────────────────────────────────────────────
    header: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      padding: '20px 24px 16px',
    },
    headerText: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    },
    title: {
      margin: 0,
      fontSize: '18px',
      fontWeight: 600,
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
    },
    subtitle: {
      margin: 0,
      fontSize: '14px',
      color: tokens.text.muted,
      fontFamily: tokens.typography.fontFamily,
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: tokens.text.muted,
      padding: '0 4px',
      lineHeight: 1,
      minWidth: '28px',
      minHeight: '28px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '6px',
      flexShrink: 0,
    },

    // ── Current Video Card ──────────────────────────────────────────────────
    currentVideoCard: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      margin: '0 24px 16px',
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.surface,
    },
    currentVideoThumbnail: {
      width: '64px',
      height: '40px',
      borderRadius: '4px',
      overflow: 'hidden',
      backgroundColor: tokens.backgrounds.surfaceAlt,
      flexShrink: 0,
    },
    currentVideoImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    currentVideoPlaceholder: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: tokens.text.muted,
    },
    currentVideoInfo: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      minWidth: 0,
    },
    currentVideoLabel: {
      fontSize: '11px',
      fontWeight: 700,
      color: '#dc2626',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
      fontFamily: tokens.typography.fontFamily,
    },
    currentVideoTitle: {
      fontSize: '13px',
      fontWeight: 600,
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    currentVideoSource: {
      fontSize: '12px',
      color: tokens.text.muted,
      fontFamily: tokens.typography.fontFamily,
    },
    removeButton: {
      padding: '6px 12px',
      fontSize: '13px',
      fontWeight: 500,
      color: tokens.ui.errorText,
      backgroundColor: 'transparent',
      border: '1px solid #fca5a5',
      borderRadius: '6px',
      cursor: 'pointer',
      flexShrink: 0,
      fontFamily: tokens.typography.fontFamily,
    },

    // ── Tabs ────────────────────────────────────────────────────────────────
    tabBar: {
      display: 'flex',
      borderBottom: `1px solid ${tokens.borders.default}`,
      padding: '0 24px',
    },
    tab: {
      padding: '12px 20px',
      fontSize: '14px',
      fontWeight: 500,
      color: tokens.text.muted,
      background: 'none',
      border: 'none',
      borderBottomWidth: '2px',
      borderBottomStyle: 'solid',
      borderBottomColor: 'transparent',
      cursor: 'pointer',
      transition: `color ${tokens.ui.transitionDefault}, border-color ${tokens.ui.transitionDefault}`,
      marginBottom: '-1px',
      minHeight: '44px',
      fontFamily: tokens.typography.fontFamily,
    },
    tabActive: {
      color: tokens.text.primary,
      borderBottomColor: tokens.text.primary,
    },

    // ── Tab content wrapper ─────────────────────────────────────────────────
    tabContent: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      overflow: 'hidden',
    },

    // ── Library tab ─────────────────────────────────────────────────────────
    libraryPanel: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      padding: '16px 24px 0',
      overflow: 'hidden',
    },
    searchWrapper: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      marginBottom: '16px',
    },
    searchIcon: {
      position: 'absolute',
      left: '14px',
      color: tokens.text.muted,
      pointerEvents: 'none',
    },
    searchInput: {
      width: '100%',
      padding: '10px 12px 10px 40px',
      fontSize: '14px',
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: '8px',
      outline: 'none',
      backgroundColor: tokens.backgrounds.page,
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
    },
    searchSpinner: {
      position: 'absolute',
      right: '14px',
      fontSize: '14px',
    },
    errorBanner: {
      marginBottom: '12px',
      padding: '10px 12px',
      backgroundColor: tokens.ui.errorBackground,
      color: tokens.ui.errorText,
      borderRadius: '6px',
      fontSize: '13px',
      fontFamily: tokens.typography.fontFamily,
    },
    resultsArea: {
      flex: 1,
      overflowY: 'auto',
      paddingRight: '4px',
      paddingBottom: '8px',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '12px',
      padding: '2px',
    },
    videoCard: {
      display: 'flex',
      flexDirection: 'column',
      borderRadius: '8px',
      overflow: 'hidden',
      backgroundColor: tokens.backgrounds.surface,
      cursor: 'pointer',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'transparent',
      transition: `border-color ${tokens.ui.transitionDefault}, box-shadow ${tokens.ui.transitionDefault}`,
    },
    videoCardSelected: {
      borderColor: tokens.ui.selectionBorder,
      boxShadow: `0 0 0 1px ${tokens.ui.selectionBorder}`,
    },
    videoCardThumbnail: {
      width: '100%',
      aspectRatio: '16/9',
      backgroundColor: tokens.backgrounds.surfaceAlt,
      position: 'relative',
      overflow: 'hidden',
    },
    videoCardImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    videoCardNoThumb: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: tokens.text.muted,
    },
    videoDurationBadge: {
      position: 'absolute',
      bottom: '6px',
      right: '6px',
      padding: '2px 6px',
      fontSize: '11px',
      fontWeight: 600,
      color: '#fff',
      backgroundColor: 'rgba(0,0,0,0.7)',
      borderRadius: '4px',
      fontFamily: tokens.typography.fontFamily,
    },
    videoSelectedBadge: {
      position: 'absolute',
      top: '6px',
      right: '6px',
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: tokens.ui.selectionBorder,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    videoCardContent: {
      padding: '8px 10px',
    },
    videoCardTitle: {
      fontSize: '12px',
      fontWeight: 500,
      color: tokens.text.primary,
      lineHeight: '1.4',
      fontFamily: tokens.typography.fontFamily,
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
    videoCardId: {
      display: 'block',
      marginTop: '2px',
      fontSize: '11px',
      color: tokens.text.muted,
      fontFamily: tokens.typography.fontFamily,
    },
    emptyState: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '200px',
    },
    emptyStateText: {
      fontSize: '14px',
      color: tokens.text.muted,
      fontFamily: tokens.typography.fontFamily,
    },

    // ── External URL tab ────────────────────────────────────────────────────
    externalUrlPanel: {
      padding: '24px',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
    externalUrlHint: {
      margin: 0,
      fontSize: '14px',
      color: tokens.text.muted,
      fontFamily: tokens.typography.fontFamily,
    },
    externalUrlInputRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    externalUrlInput: {
      flex: 1,
      padding: '10px 14px',
      fontSize: '14px',
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: '8px',
      outline: 'none',
      backgroundColor: tokens.backgrounds.page,
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
    },
    previewButton: {
      padding: '10px 20px',
      fontSize: '14px',
      fontWeight: 500,
      color: tokens.text.primary,
      backgroundColor: 'transparent',
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: '8px',
      cursor: 'pointer',
      fontFamily: tokens.typography.fontFamily,
      flexShrink: 0,
    },
    externalUrlErrorText: {
      margin: 0,
      fontSize: '13px',
      color: tokens.ui.errorText,
      fontFamily: tokens.typography.fontFamily,
    },

    // ── Preview card ────────────────────────────────────────────────────────
    previewCard: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px',
      borderRadius: '8px',
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.surface,
    },
    previewThumbnail: {
      width: '80px',
      height: '52px',
      borderRadius: '6px',
      backgroundColor: '#0C447C',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      flexShrink: 0,
      overflow: 'hidden',
    },
    previewThumbnailImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    previewInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      minWidth: 0,
    },
    previewTitle: {
      fontSize: '14px',
      fontWeight: 600,
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    previewMeta: {
      fontSize: '12px',
      color: tokens.text.muted,
      fontFamily: tokens.typography.fontFamily,
    },

    // ── Footer ──────────────────────────────────────────────────────────────
    footer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '12px',
      padding: '16px 24px',
      borderTop: `1px solid ${tokens.borders.default}`,
    },
    footerHint: {
      fontSize: '12px',
      color: tokens.text.muted,
      fontFamily: tokens.typography.fontFamily,
    },
    footerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    cancelButton: {
      padding: '8px 20px',
      fontSize: '14px',
      fontWeight: 500,
      color: tokens.text.primary,
      backgroundColor: 'transparent',
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: '8px',
      cursor: 'pointer',
      fontFamily: tokens.typography.fontFamily,
    },
    confirmButton: {
      padding: '8px 20px',
      fontSize: '14px',
      fontWeight: 500,
      color: '#fff',
      backgroundColor: tokens.text.primary,
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontFamily: tokens.typography.fontFamily,
    },
  };
}
